import { useState } from 'react';
import { Mail, Shield, User, Copy, Check, Send } from 'lucide-react';
import { Modal, Button, Input } from '@/components/ui';
import { useSettingsStore } from '@/stores';
import { useToast } from '@/components/ui/Toast';
import type { UserRole } from '@perfin/shared';

interface InvitationFormProps {
  onClose: () => void;
}

export function InvitationForm({ onClose }: InvitationFormProps) {
  const { inviteUser, organization } = useSettingsStore();
  const toast = useToast();

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole>('standard');
  const [sending, setSending] = useState(false);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      toast.error('Email is required');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSending(true);
    try {
      const token = await inviteUser(email.trim().toLowerCase(), role);

      // Generate invite link
      const baseUrl = window.location.origin;
      const link = `${baseUrl}/invite/${token}`;
      setInviteLink(link);

      toast.success('Invitation created - share the link below');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to send invitation');
    } finally {
      setSending(false);
    }
  };

  const handleCopyLink = async () => {
    if (!inviteLink) return;

    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleSendAnother = () => {
    setEmail('');
    setRole('standard');
    setInviteLink(null);
  };

  const handleShareViaEmail = () => {
    if (!inviteLink) return;
    const subject = encodeURIComponent(`You're invited to join ${organization?.name || 'our organization'} on PerFin`);
    const body = encodeURIComponent(
      `Hi,\n\nYou've been invited to join ${organization?.name || 'our organization'} on PerFin.\n\nClick the link below to accept the invitation:\n${inviteLink}\n\nThis link expires in 7 days.\n\nBest regards`
    );
    window.open(`mailto:${email}?subject=${subject}&body=${body}`, '_blank');
  };

  // Success state - show invite link
  if (inviteLink) {
    return (
      <Modal isOpen onClose={onClose} title="Invitation Ready">
        <div className="space-y-6">
          <div className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
              <Check className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">Invitation Created</h3>
            <p className="text-sm text-gray-500 mt-1">
              Copy the link below or share via email to invite <strong>{email}</strong> to {organization?.name || 'your organization'}.
            </p>
          </div>

          {/* Invite Link */}
          <div className="bg-gray-50 rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Invitation Link
            </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={inviteLink}
                className="flex-1 text-sm bg-white border border-gray-300 rounded-lg px-3 py-2 focus:outline-none"
              />
              <Button
                onClick={handleCopyLink}
                variant="secondary"
                leftIcon={copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              >
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              This link expires in 7 days
            </p>
          </div>

          {/* Share via Email */}
          <Button
            onClick={handleShareViaEmail}
            variant="primary"
            leftIcon={<Send className="h-4 w-4" />}
            className="w-full"
          >
            Share via Email
          </Button>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="secondary" onClick={handleSendAnother}>
              Invite Another
            </Button>
            <Button variant="secondary" onClick={onClose}>
              Done
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  // Form state
  return (
    <Modal isOpen onClose={onClose} title="Invite Member">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Email Input */}
        <Input
          type="email"
          label="Email Address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="colleague@example.com"
          leftIcon={<Mail className="h-5 w-5 text-gray-400" />}
          autoFocus
        />

        {/* Role Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Role
          </label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setRole('admin')}
              className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                role === 'admin'
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div
                className={`p-2 rounded-full ${
                  role === 'admin' ? 'bg-primary-100' : 'bg-gray-100'
                }`}
              >
                <Shield
                  className={`h-5 w-5 ${
                    role === 'admin' ? 'text-primary-600' : 'text-gray-500'
                  }`}
                />
              </div>
              <div className="text-left">
                <p className="font-medium text-gray-900">Admin</p>
                <p className="text-xs text-gray-500">Full access</p>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setRole('standard')}
              className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                role === 'standard'
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div
                className={`p-2 rounded-full ${
                  role === 'standard' ? 'bg-primary-100' : 'bg-gray-100'
                }`}
              >
                <User
                  className={`h-5 w-5 ${
                    role === 'standard' ? 'text-primary-600' : 'text-gray-500'
                  }`}
                />
              </div>
              <div className="text-left">
                <p className="font-medium text-gray-900">Standard</p>
                <p className="text-xs text-gray-500">Limited access</p>
              </div>
            </button>
          </div>

          {/* Role description */}
          <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
            {role === 'admin' ? (
              <ul className="list-disc list-inside space-y-1">
                <li>Can view and edit all items (including Admin Only items)</li>
                <li>Can manage categories and accounts</li>
                <li>Can invite and manage other users</li>
              </ul>
            ) : (
              <ul className="list-disc list-inside space-y-1">
                <li>Can view items not marked as Admin Only</li>
                <li>Can only edit items they own or created</li>
                <li>Cannot manage users or organization settings</li>
              </ul>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={sending}>
            Create Invitation
          </Button>
        </div>
      </form>
    </Modal>
  );
}
